import { useMemo } from "react";
import { Building2, Trophy, Medal, Award } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { fmtInt } from "@/lib/format";

const MEDALS = [
  { icon: Trophy, color: "text-amber-400", bg: "bg-amber-400/15", ring: "border-amber-400/40" },
  { icon: Medal, color: "text-slate-300", bg: "bg-slate-300/15", ring: "border-slate-300/40" },
  { icon: Award, color: "text-orange-500", bg: "bg-orange-500/15", ring: "border-orange-500/40" },
];

export default function RankingCondominios({ clientes = [], monthLabel = "", onSelect = null }) {
  const ranking = useMemo(() => {
    const map = new Map();
    for (const c of clientes) {
      const key = (c.condominio || "").trim();
      if (!key) continue;
      const acc = map.get(key) || { nome: key, total: 0, planos: {} };
      acc.total += 1;
      const plano = c.plano_escolhido || "—";
      acc.planos[plano] = (acc.planos[plano] || 0) + 1;
      map.set(key, acc);
    }
    const arr = [...map.values()].sort((a, b) => b.total - a.total).slice(0, 5);
    const max = arr[0]?.total || 1;
    return arr.map((v) => ({ ...v, pct: (v.total / max) * 100 }));
  }, [clientes]);

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="ranking-condominios">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Top condomínios por vendas</h3>
        </div>
        <Badge variant="outline">{monthLabel || "Todos os meses"}</Badge>
      </div>
      {ranking.length === 0 && (
        <div className="text-sm text-muted-foreground">
          Nenhum cliente vinculado a condomínio no período. Vincule clientes ao cadastrá-los.
        </div>
      )}
      <div className="space-y-3">
        {ranking.map((v, idx) => {
          const medal = MEDALS[idx];
          const Icon = medal?.icon;
          const clickable = typeof onSelect === "function";
          const RowTag = clickable ? "button" : "div";
          return (
            <RowTag
              key={v.nome}
              type={clickable ? "button" : undefined}
              onClick={clickable ? () => onSelect(v.nome) : undefined}
              data-testid={`rank-condo-${idx + 1}`}
              className={`w-full text-left rounded-lg border p-4 flex items-center gap-4 transition-all
                ${idx === 0 ? "border-primary/40 bg-primary/5" : "border-border bg-background"}
                ${clickable ? "hover:border-primary/60 hover:bg-primary/5 hover:shadow-md active:scale-[0.995] cursor-pointer" : ""}`}
            >
              <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${medal?.bg || "bg-muted"}`}>
                {Icon ? <Icon className={`w-5 h-5 ${medal.color}`} /> : <span className="font-bold text-muted-foreground">{idx + 1}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-heading font-semibold truncate">{v.nome}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {Object.entries(v.planos).map(([p, n]) => `${p}: ${n}`).join(" · ")}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-2xl font-extrabold tabular-nums text-primary">{fmtInt(v.total)}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">clientes</div>
                  </div>
                </div>
                <Progress value={v.pct} className="h-2 mt-3" />
              </div>
            </RowTag>
          );
        })}
      </div>
    </div>
  );
}
